/**
 * commitMapping — wire→contract adapter for the commit-pipeline endpoints.
 *
 * WHY THIS EXISTS (root-cause fix, 2026-06-25):
 *   The Function wraps every commit response in a `{ success, data, error, code }`
 *   envelope, and its DTOs (`CommitStatusResponse` / `CommitCartResponse`,
 *   apps/PCBJxChangeApi/Models/CartCommitModels.cs) use a DIFFERENT field +
 *   step vocabulary than the shared-types contract the UI is written against:
 *     • the cart status is `data.status`, the UI reads `cartStatus`
 *     • steps carry `stepName` (server kebab), the UI reads `stepKey` (its own
 *       12-key union — 5 of 12 keys differ, e.g. validate↔pre-commit-validation)
 *     • step status adds `short-circuited`; the UI union doesn't have it
 *     • the active step is a numeric `currentStepIndex`, the UI reads `currentStepKey`
 *   The transport (`http.ts`) returns the raw body, so `getCommitStatus()` was
 *   handing the UI the *envelope* — `snap.cartStatus` was therefore ALWAYS
 *   undefined. CommitFlow's poll treats `undefined !== cart.status` as a real
 *   transition and called reload() every 2s forever (the "tab refreshes every
 *   2 seconds" bug). This module unwraps the envelope and translates the wire
 *   shape into the canonical `CommitStatus` / `CommitOutcome` types so the poll,
 *   the progress bar, and the awaiting-signatures banner all see real data.
 *
 * Forward-compatible: if the Function is later updated to emit the canonical
 * field names, the step map simply falls through (server name === frontend key)
 * and the status passes straight through — the adapter becomes a no-op.
 */

import {
  asIso8601,
  type CartCompletionSummary,
  type CommitStatus,
  type CommitOutcome,
  type CommitPipelineStep,
  type CommitPipelineStepKey,
  type CommitPipelineStepStatus,
  type CompletionSummaryAccount,
  type CartStatus,
} from '@retail-ops/shared-types';

// ── Raw wire shapes (camelCased by the Function's JsonNamingPolicy.CamelCase) ──

interface WireStep {
  stepIndex?: number;
  stepName?: string;
  status?: string;
  durationMs?: number | null;
  idempotencyKey?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  detailLabel?: string | null;
}

/** GET /commit/status inner payload (CommitStatusResponse). */
interface WireCommitStatus {
  success?: boolean;
  cartId?: string;
  status?: string; // cart status (kebab) — NOT the UI's `cartStatus`
  resolutionCode?: string | null;
  commitStartedAt?: string | null;
  commitCompletedAt?: string | null;
  awaitingSignaturesAt?: string | null;
  currentStepIndex?: number | null;
  totalSteps?: number;
  attemptCount?: number;
  lastErrorAt?: string | null;
  lastErrorMsg?: string | null;
  signingGroupId?: string | null;
  signaturesCollected?: number | null;
  signaturesTotal?: number | null;
  /** Completion confirmation record (round-2 smoke, 2026-07-10) — raw JSON blob. */
  completionSummary?: unknown;
  steps?: WireStep[];
  /** REQ-167 — granular per-call post-sign timeline + wedge detection. */
  granularSteps?: WireGranularStep[] | null;
  wedged?: boolean;
  wedgedReason?: string | null;
  error?: string | null;
  errorCode?: string | null;
  statusCode?: number | null;
}

/** REQ-167 — wire shape of one granular snapshot step (GranularStepDto). */
interface WireGranularStep {
  index?: number;
  type?: string;
  plainLabel?: string;
  state?: string;
  resultId?: string | null;
  detail?: string | null;
}

/** POST /commit (+ retry/abort) inner payload (CommitCartResponse). */
interface WireCommitCart {
  success?: boolean;
  cartId?: string;
  status?: string;
  awaitingSignatures?: boolean;
  signingGroupId?: string | null;
  steps?: WireStep[];
  error?: string | null;
  errorCode?: string | null;
  statusCode?: number | null;
  wasTestMode?: boolean;
}

/** Every commit endpoint wraps its payload in this envelope. */
export interface CommitEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: string | null;
  code?: string | null;
}

// ── Step vocabulary translation (server CommitPipelineSteps.InOrder → UI keys) ──

/** Server canonical 12-step order — index-aligned with CommitPipelineSteps.InOrder. */
const SERVER_STEP_ORDER: ReadonlyArray<string> = [
  'validate',
  'cif-create',
  'acct-id-gen',
  'disclosure-prefill',
  'signnow-dispatch',
  'await-signatures',
  'acct-add',
  'beneficiary-attach',
  'post-sign-jxchange',
  'qc-dataverse-write',
  'notifications',
  'resolution',
];

/** server stepName → UI CommitPipelineStepKey (5 of 12 differ). */
const SERVER_TO_UI_STEP: Record<string, CommitPipelineStepKey> = {
  'validate': 'pre-commit-validation',
  'cif-create': 'cif-create',
  'acct-id-gen': 'acct-id-gen',
  'disclosure-prefill': 'disclosure-pre-fill',
  'signnow-dispatch': 'signing-dispatch',
  'await-signatures': 'await-signatures',
  'acct-add': 'acct-add',
  'beneficiary-attach': 'beneficiary-attach',
  'post-sign-jxchange': 'post-sign-jx',
  'qc-dataverse-write': 'qc-dataverse-write',
  'notifications': 'notifications',
  'resolution': 'cart-resolution-stamp',
  'account-create': 'account-create', // Wave-0 legacy alias
};

function toUiStepKey(serverName: string | undefined | null): CommitPipelineStepKey | undefined {
  if (!serverName) return undefined;
  // Fall through to the raw value (already a UI key) when unmapped — keeps the
  // adapter a no-op if the Function later emits the canonical names.
  return SERVER_TO_UI_STEP[serverName] ?? (serverName as CommitPipelineStepKey);
}

/** server/db step status → UI CommitPipelineStepStatus closed union. */
const STEP_STATUS_MAP: Record<string, CommitPipelineStepStatus> = {
  'success': 'success',
  // 2026-07-02 (live-walk fix): the Function's PipelineStepExecutions rows use
  // 'succeeded' — the missing alias made every completed step render as an
  // empty pending circle even though /commit/status reported it done.
  'succeeded': 'success',
  'complete': 'success',
  'completed': 'success',
  'short-circuited': 'success', // idempotent re-run that was already done
  'in-progress': 'in-progress',
  'running': 'in-progress',
  'failed': 'failed',
  'error': 'failed',
  'skipped': 'skipped',
  'paused': 'paused',
  'pending': 'pending',
};

function toStepStatus(s: string | undefined | null): CommitPipelineStepStatus {
  if (!s) return 'pending';
  return STEP_STATUS_MAP[s.toLowerCase()] ?? 'pending';
}

function mapSteps(
  wire: WireStep[] | undefined,
  cartStatus: string | undefined,
): CommitPipelineStep[] {
  if (!Array.isArray(wire)) return [];
  const out: CommitPipelineStep[] = [];
  for (const w of wire) {
    const stepKey = toUiStepKey(w.stepName);
    if (!stepKey) continue;
    let status = toStepStatus(w.status);
    // The pipeline parks on await-signatures; render the pause glyph there when
    // the cart is actually waiting (the DB row is usually still 'pending'/'running').
    if (
      stepKey === 'await-signatures'
      && cartStatus === 'awaiting-signatures'
      && (status === 'in-progress' || status === 'pending')
    ) {
      status = 'paused';
    }
    const step: CommitPipelineStep = {
      stepKey,
      stepIndex: typeof w.stepIndex === 'number' ? w.stepIndex : out.length + 1,
      status,
    };
    if (w.errorCode) step.errorCode = w.errorCode;
    if (w.errorMessage) step.errorMessage = w.errorMessage;
    if (w.detailLabel) step.detailLabel = w.detailLabel;
    out.push(step);
  }
  return out;
}

function currentStepKeyFromIndex(idx: number | null | undefined): CommitPipelineStepKey | undefined {
  // currentStepIndex is 1-based (cart.PipelineStepIndex; 6 = await-signatures).
  if (typeof idx !== 'number' || idx < 1 || idx > SERVER_STEP_ORDER.length) return undefined;
  return toUiStepKey(SERVER_STEP_ORDER[idx - 1]);
}

// ── Completion confirmation record (Bill smoke-test round 2, 2026-07-10) ──────
// The Function serializes MetadataJson.completionSummary verbatim onto the
// status payload. Validate defensively — a malformed blob maps to undefined,
// never a throw mid-poll.

function stringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((s): s is string => typeof s === 'string' && s.length > 0);
  return out;
}

function mapCompletionSummary(raw: unknown): CartCompletionSummary | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const src = raw as Record<string, unknown>;
  const out: CartCompletionSummary = {};

  if (Array.isArray(src['accountsCreated'])) {
    const accounts: CompletionSummaryAccount[] = [];
    for (const a of src['accountsCreated'] as unknown[]) {
      if (!a || typeof a !== 'object') continue;
      const rec = a as Record<string, unknown>;
      const acctId = typeof rec['acctId'] === 'string' ? rec['acctId'] : undefined;
      if (!acctId) continue;
      const acct: CompletionSummaryAccount = { acctId };
      if (typeof rec['acctType'] === 'string') acct.acctType = rec['acctType'];
      if (typeof rec['productCode'] === 'string') acct.productCode = rec['productCode'];
      accounts.push(acct);
    }
    out.accountsCreated = accounts;
  }

  const cifs = stringArray(src['cifs']);
  if (cifs) out.cifs = cifs;
  const created = stringArray(src['cifsCreated']);
  if (created) out.cifsCreated = created;
  const updated = stringArray(src['cifsUpdated']);
  if (updated) out.cifsUpdated = updated;
  if (typeof src['docsSigned'] === 'number') out.docsSigned = src['docsSigned'];
  if (typeof src['committedAt'] === 'string') out.committedAt = asIso8601(src['committedAt']);

  // Treat a record with nothing usable as absent.
  return Object.keys(out).length > 0 ? out : undefined;
}

// ── Public mappers ────────────────────────────────────────────────────────────

/**
 * Map the GET /commit/status response (enveloped OR a bare inner payload) into
 * the canonical `CommitStatus`. Tolerant of a missing/failed envelope: returns
 * an empty `cartStatus` so callers (and CommitFlow's truthy-guard) treat it as
 * "no transition" rather than looping.
 */
export function mapCommitStatus(raw: unknown): CommitStatus {
  const env = (raw ?? {}) as CommitEnvelope<WireCommitStatus> | WireCommitStatus;
  const data: WireCommitStatus =
    env && typeof env === 'object' && 'data' in env && env.data
      ? (env.data as WireCommitStatus)
      : ((env as WireCommitStatus) ?? {});

  const cartStatus = (data.status ?? '') as CartStatus;
  const out: CommitStatus = {
    cartId: data.cartId ?? '',
    cartStatus,
    steps: mapSteps(data.steps, data.status),
  };
  const currentStepKey = currentStepKeyFromIndex(data.currentStepIndex);
  if (currentStepKey) out.currentStepKey = currentStepKey;
  if (data.awaitingSignaturesAt) out.awaitingSignaturesAt = asIso8601(data.awaitingSignaturesAt);
  if (typeof data.signaturesCollected === 'number') out.signaturesCollected = data.signaturesCollected;
  if (typeof data.signaturesTotal === 'number') out.signaturesTotal = data.signaturesTotal;
  if (data.lastErrorMsg) out.lastPipelineErrorMsg = data.lastErrorMsg;
  if (data.errorCode) out.lastPipelineErrorCode = data.errorCode;
  const completionSummary = mapCompletionSummary(data.completionSummary);
  if (completionSummary) out.completionSummary = completionSummary;
  // REQ-167 — granular timeline + wedge flag.
  if (Array.isArray(data.granularSteps) && data.granularSteps.length > 0) {
    out.granularSteps = data.granularSteps.map((g) => ({
      index: typeof g.index === 'number' ? g.index : 0,
      type: g.type ?? '',
      plainLabel: g.plainLabel ?? g.type ?? '',
      state: g.state ?? 'pending',
      ...(g.resultId ? { resultId: g.resultId } : {}),
      ...(g.detail ? { detail: g.detail } : {}),
    }));
  }
  if (data.wedged === true) {
    out.wedged = true;
    if (data.wedgedReason) out.wedgedReason = data.wedgedReason;
  }
  return out;
}

/**
 * Map the POST /commit (+ retry/abort) envelope into the canonical
 * `CommitOutcome`. `cart` is intentionally absent — the commit response does
 * not carry a full CartDto; callers re-fetch via their onCartUpdated path.
 */
export function mapCommitOutcome(raw: unknown): CommitOutcome {
  const env = (raw ?? {}) as CommitEnvelope<WireCommitCart>;
  const data = env?.data;
  const out: CommitOutcome = {
    success: !!env?.success && (data?.success ?? true),
  };
  if (data) out.status = mapCommitStatus(data);
  const error = env?.error ?? data?.error;
  if (error) out.error = error;
  const code = env?.code ?? data?.errorCode;
  if (code) out.errorCode = code;
  return out;
}
